import useSWR from 'swr'

interface Manufacturer {
  id: number
  name: string
  website: string | null
  logo_url: string | null
  product_count: number | null
  normalized_name: string | null
}

interface ManufacturerWithLogo {
  id: number
  name: string
  product_count: number
  logo_url: string | null
}

const fetcher = (url: string) => fetch(url).then(r => r.json())

/**
 * Custom hook for fetching all manufacturers
 * Uses SWR with longer deduplication interval since manufacturers change infrequently
 */
export function useManufacturers() {
  const { data, error, isLoading } = useSWR<Manufacturer[]>(
    '/api/manufacturers',
    fetcher,
    {
      revalidateOnFocus: false, // Don't revalidate when window regains focus
      revalidateOnReconnect: false, // Don't revalidate on reconnect (data is relatively static)
      dedupingInterval: 60000, // Dedupe requests within 60 seconds (1 minute)
      revalidateIfStale: false, // Don't revalidate stale data automatically
      revalidateOnMount: true, // Always revalidate on mount
    }
  )
  
  return {
    manufacturers: data || [],
    isLoading,
    isError: error,
    error,
  }
}

/**
 * Custom hook for fetching a single manufacturer by ID
 */
export function useManufacturer(id: number | null | undefined) {
  const { data, error, isLoading } = useSWR<Manufacturer>(
    id ? `/api/manufacturers/${id}` : null,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 60000,
      revalidateIfStale: false,
    }
  )
  
  return {
    manufacturer: data || null,
    isLoading,
    isError: error,
    error,
  }
}

/**
 * Custom hook for fetching top manufacturers with their logo URLs
 * Used specifically for the manufacturers carousel on the homepage
 * Returns top 12 manufacturers by product count with extracted logo URLs
 */
export function useManufacturersWithLogos() {
  const { data, error, isLoading } = useSWR<ManufacturerWithLogo[]>(
    '/api/manufacturers/logos',
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 300000, // 5 minutes - logos change very infrequently
      revalidateIfStale: false,
      revalidateOnMount: true,
    }
  )
  
  return {
    manufacturers: data || [],
    isLoading,
    isError: error,
    error,
  }
}
